import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, MapPin, MessageCircle, Clock, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function ContactPage() {
  const [settings, setSettings] = useState(null);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message envoyé !");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const phoneRaw = (settings?.phone || "01 80 81 43 65").replace(/\s/g, "");
  const waRaw = (settings?.whatsapp || "+33180814365").replace(/[+\s]/g, "");

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="contact-page">
      <p className="gold-label mb-3">Nous trouver</p>
      <h1 className="font-serif-elegant text-4xl sm:text-5xl font-bold mb-12">Contact</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="space-y-6">
          <div className="ic-card !transform-none p-7">
            <div className="flex items-start gap-4">
              <MapPin className="text-[#D4AF37] mt-1" size={22} />
              <div>
                <p className="font-medium">Italian Club — Champs-sur-Marne</p>
                <p className="text-sm text-gray-400 mt-1">{settings?.address}</p>
                <p className="text-xs text-gray-500 mt-1">À côté du parking gratuit du château</p>
                <a href={settings?.maps_url} target="_blank" rel="noreferrer" data-testid="contact-maps-link" className="text-[#D4AF37] text-sm hover:underline mt-2 inline-block">
                  Ouvrir dans Google Maps →
                </a>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <a href={`tel:${phoneRaw}`} data-testid="contact-call-btn" className="btn-gold !py-4 justify-center">
              <Phone size={18} /> Appeler
            </a>
            <a href={`https://wa.me/${waRaw}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn" className="btn-outline-gold !py-4 justify-center">
              <MessageCircle size={18} /> WhatsApp
            </a>
          </div>
          <div className="ic-card !transform-none p-7">
            <div className="flex items-center gap-3 mb-5">
              <Clock className="text-[#D4AF37]" size={20} />
              <p className="font-serif-elegant text-xl font-semibold">Horaires d'ouverture</p>
            </div>
            <ul className="space-y-2.5 text-sm" data-testid="contact-opening-hours">
              {Object.entries(settings?.opening_hours || {}).map(([day, h]) => (
                <li key={day} className="flex justify-between">
                  <span className="text-gray-200">{day}</span>
                  <span className="text-gray-400">{h}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl overflow-hidden border border-white/10">
            <iframe
              title="Google Maps Italian Club"
              src="https://www.google.com/maps?q=36+Rue+de+Paris+77420+Champs-sur-Marne&output=embed"
              className="w-full h-64"
              loading="lazy"
              allowFullScreen
            />
          </div>
        </div>
        <div className="space-y-6">
          <form onSubmit={submit} className="ic-card !transform-none p-8" data-testid="contact-form">
            <h2 className="font-serif-elegant text-2xl font-semibold mb-6">Envoyez-nous un message</h2>
            {done ? (
              <div className="text-center py-10" data-testid="contact-confirmation">
                <CheckCircle2 size={48} className="text-[#009246] mx-auto mb-4" />
                <p className="text-gray-200">Merci ! Nous vous répondrons rapidement.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <input data-testid="contact-name" required className="ic-input" placeholder="Nom complet *" value={form.name} onChange={set("name")} />
                <input data-testid="contact-email" required type="email" className="ic-input" placeholder="Email *" value={form.email} onChange={set("email")} />
                <input data-testid="contact-phone" type="tel" className="ic-input" placeholder="Téléphone" value={form.phone} onChange={set("phone")} />
                <textarea data-testid="contact-message" required rows="5" className="ic-input" placeholder="Votre message *" value={form.message} onChange={set("message")} />
                <button type="submit" data-testid="contact-submit-btn" className="btn-gold w-full !py-3.5">Envoyer</button>
              </div>
            )}
          </form>
          <div className="ic-card !transform-none p-7">
            <p className="font-serif-elegant text-xl font-semibold mb-2">Un événement privé en tête ?</p>
            <p className="text-sm text-gray-400 mb-4">Mariages, anniversaires, repas d'entreprise… Demandez votre devis personnalisé.</p>
            <Link to="/terrasse-evenements" data-testid="contact-private-event-link" className="btn-outline-gold !py-2 text-sm">Demander un devis</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
